import {
  CdkDragDrop,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { Field } from '../../../models/reports/field.model';
import { ReportField } from '../../../models/api/reports/report';
import { Report } from '../../../models/api/reports/report';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { ReportsService } from '../../../services/reports/reports.service';
import { scheduleRestoreMoveButtonFocus, announceListItemMoved } from '../../../functions/list-reorder';
import { ReportFieldDialogComponent } from './report-field-dialog.component';
import {
  ReorderableFieldItem,
  ReorderableFieldListComponent,
} from './reorderable-field-list.component';
import { LiveAnnouncer } from '@angular/cdk/a11y';

@Component({
  selector: 'app-drag-drop-fields',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReportFieldDialogComponent, ReorderableFieldListComponent],
  template: `
    <app-report-field-dialog #fieldDialog (selected)="selected($event)" />
    @if (editableFieldCount() >= 1) {
      <div class="tw-italic tw-pb-4">
        Click the edit icon in a tile to change the field. Locked fields cannot
        be changed.
        @if (editableFieldCount() >= 2) {
          Editable fields can be reordered using the move buttons or drag and
          drop.
        }
      </div>
    }

    @if (hasMultipleSections()) {
      <fieldset class="tw-border tw-border-gray-300 tw-rounded tw-p-4 tw-mb-4">
        <legend class="tw-sr-only">Default fields</legend>
        <app-reorderable-field-list
          layout="grid"
          [columns]="8"
          [items]="toListItems(defaultFields())"
          [dropListData]="defaultFields()"
          [showClearOnTile]="true"
          (edit)="onListEdit($event.index, defaultFields())"
          (clear)="onListClear($event.index, defaultFields())"
          (move)="
            moveFieldInSection(
              'default',
              $event.item.id,
              $event.delta,
              $event.event
            )
          "
          (dropped)="dropInSection($event, 'default')"
        />
      </fieldset>

      @if (hasWcSection()) {
        <fieldset
          class="tw-border tw-border-gray-300 tw-rounded tw-p-4 tw-mb-4"
        >
          <legend class="tw-text-sm tw-font-semibold tw-px-2">
            Workers' compensation only
          </legend>
          <app-reorderable-field-list
            layout="grid"
            [columns]="8"
            [items]="toListItems(wcFields())"
            [dropListData]="wcFields()"
            [showClearOnTile]="true"
            (edit)="onListEdit($event.index, wcFields())"
            (clear)="onListClear($event.index, wcFields())"
            (move)="
              moveFieldInSection(
                'WC',
                $event.item.id,
                $event.delta,
                $event.event
              )
            "
            (dropped)="dropInSection($event, 'WC')"
          />
        </fieldset>
      }

      @if (hasLiSection()) {
        <fieldset
          class="tw-border tw-border-gray-300 tw-rounded tw-p-4 tw-mb-4"
        >
          <legend class="tw-text-sm tw-font-semibold tw-px-2">
            Liability only
          </legend>
          <app-reorderable-field-list
            layout="grid"
            [columns]="8"
            [items]="toListItems(liFields())"
            [dropListData]="liFields()"
            [showClearOnTile]="true"
            (edit)="onListEdit($event.index, liFields())"
            (clear)="onListClear($event.index, liFields())"
            (move)="
              moveFieldInSection(
                'LI',
                $event.item.id,
                $event.delta,
                $event.event
              )
            "
            (dropped)="dropInSection($event, 'LI')"
          />
        </fieldset>
      }

      @if (hasPrSection()) {
        <fieldset
          class="tw-border tw-border-gray-300 tw-rounded tw-p-4 tw-mb-4"
        >
          <legend class="tw-text-sm tw-font-semibold tw-px-2">
            Property only
          </legend>
          <app-reorderable-field-list
            layout="grid"
            [columns]="8"
            [items]="toListItems(prFields())"
            [dropListData]="prFields()"
            [showClearOnTile]="true"
            (edit)="onListEdit($event.index, prFields())"
            (clear)="onListClear($event.index, prFields())"
            (move)="
              moveFieldInSection(
                'PR',
                $event.item.id,
                $event.delta,
                $event.event
              )
            "
            (dropped)="dropInSection($event, 'PR')"
          />
        </fieldset>
      }
    } @else {
      <fieldset class="tw-border-0 tw-p-0 tw-m-0">
        <legend class="tw-sr-only">Report fields</legend>
        <app-reorderable-field-list
          layout="grid"
          [columns]="8"
          [items]="toListItems(items(), '56px')"
          [dropListData]="items()"
          [showClearOnTile]="true"
          (edit)="onListEdit($event.index, items())"
          (clear)="onListClear($event.index, items())"
          (move)="moveField($event.item.id, $event.delta, $event.event)"
          (dropped)="drop($event)"
        />
      </fieldset>
    }
  `,
})
export class DragDropFieldsComponent {
  @ViewChild('fieldDialog')
  dialog: ReportFieldDialogComponent;

  // Inputs
  report = input.required<Report>();

  // Outputs
  fieldsChanged = output<void>();

  // Services
  private snackbar = inject(Snackbar);
  private reports = inject(ReportsService);
  private liveAnnouncer = inject(LiveAnnouncer);

  // State
  items = signal<Field[]>([]);
  currentItem: Field | null = null;

  defaultFields = computed(() =>
    this.items().filter((f) => !f.metadata?.customSection),
  );

  wcFields = computed(() =>
    this.items().filter((f) => f.metadata?.customSection === 'WC'),
  );

  liFields = computed(() =>
    this.items().filter((f) => f.metadata?.customSection === 'LI'),
  );

  prFields = computed(() =>
    this.items().filter((f) => f.metadata?.customSection === 'PR'),
  );

  hasWcSection = computed(() => this.wcFields().length > 0);
  hasLiSection = computed(() => this.liFields().length > 0);
  hasPrSection = computed(() => this.prFields().length > 0);
  hasMultipleSections = computed(
    () => this.hasWcSection() || this.hasLiSection() || this.hasPrSection(),
  );

  editableFieldCount = computed(
    () => this.items().filter((f) => f.updateable).length,
  );

  toListItems(list: Field[], minHeight = '40px'): ReorderableFieldItem[] {
    const canReorder = this.editableFieldCount() >= 2;
    return list.map((item, i) => ({
      id: item.id,
      label: item.field ? item.label : item.label || 'Empty',
      hasField: !!item.field && !item.isPlaceholder,
      disabled: item.disabled,
      updateable: item.updateable,
      showLockIcon: true,
      dragDisabled: item.disabled,
      showMove: canReorder && item.updateable && !item.disabled,
      disableEarlier: this.neighborIndex(list, i, -1) === -1,
      disableLater: this.neighborIndex(list, i, 1) === -1,
      showRemove: false,
      fixedWidth: true,
      wrapLabel: true,
      minHeight,
    }));
  }

  onListEdit(index: number, list: Field[]): void {
    const item = list[index];
    if (item) this.setField(item);
  }

  onListClear(index: number, list: Field[]): void {
    const item = list[index];
    if (item) this.clearField(item);
  }

  constructor() {
    // Initialize fields when report changes
    effect(() => {
      const currentReport = this.report();
      this.initializeFields(currentReport);
    });
  }

  private isDyoOutputField(field: ReportField): boolean {
    const label = (field.defaultValue ?? '').trim();
    return (
      /^Financial\s+\d+$/i.test(label) ||
      /^Non-Financial\s+\d+$/i.test(label) ||
      /^Diff\s+in\s+\d+$/i.test(label)
    );
  }

  private initializeFields(report: Report): void {
    let reportFields = report.reportRequest?.reportFields || [];

    if (
      report.reportSections?.hasCustomFinancials ||
      report.reportSections?.hasCustomNonFinancials
    ) {
      reportFields = reportFields.filter(
        (f) =>
          f.inlineFilterType !== 'COMP_FINANCIAL' &&
          f.inlineFilterType !== 'COMP_NON_FINANCIAL' &&
          !this.isDyoOutputField(f),
      );
    }

    const newItems: Field[] = [];

    reportFields.forEach((field, index) => {
      if (!field) return;

      const columnName =
        field.columnName ||
        `NULL_${String(field.displayOrder ?? index + 1).padStart(2, '0')}`;

      const completeMetadata: ReportField = {
        canDelete: field.canDelete,
        columnId: field.columnId,
        columnName: columnName,
        customSection: field.customSection,
        defaultValue: field.defaultValue,
        displayOrder: field.displayOrder,
        inlineFilterType: field.inlineFilterType,
        pageBreakChecked: field.pageBreakChecked,
        tableAlias: field.tableAlias,
        updateable: field.updateable,
        selectedFieldLabel: field.selectedFieldLabel,
      };

      const label =
        completeMetadata.selectedFieldLabel || completeMetadata.defaultValue;

      const isNullColumn = !!completeMetadata.columnName?.includes('NULL_');
      const isCustomFieldLabel = !!label?.startsWith('Custom Field');
      const isPlaceholder = isNullColumn || isCustomFieldLabel;

      const fieldValue = completeMetadata.columnName;

      newItems.push(
        new Field(
          label || 'Empty',
          fieldValue,
          !completeMetadata.updateable,
          completeMetadata.canDelete,
          completeMetadata.updateable,
          completeMetadata,
          isPlaceholder,
        ),
      );
    });

    this.items.set(newItems);
  }

  drop(event: CdkDragDrop<unknown>): void {
    const items = this.items();
    if (items[event.currentIndex].disabled) {
      this.snackbar.show('Locked fields cannot be reordered', 'Info');
      return;
    }

    const enabledItems: Field[] = [];
    const disabledPositions = new Map<number, Field>();

    items.forEach((item, idx) => {
      if (item.disabled) {
        disabledPositions.set(idx, item);
      } else {
        enabledItems.push(item);
      }
    });

    let enabledPrevIdx = 0;
    let enabledCurrIdx = 0;
    let enabledCount = 0;
    for (let i = 0; i < items.length; i++) {
      if (!items[i].disabled) {
        if (i === event.previousIndex) enabledPrevIdx = enabledCount;
        if (i === event.currentIndex) enabledCurrIdx = enabledCount;
        enabledCount++;
      }
    }

    moveItemInArray(enabledItems, enabledPrevIdx, enabledCurrIdx);

    const newItems: Field[] = [];
    let eIdx = 0;
    for (let i = 0; i < items.length; i++) {
      if (disabledPositions.has(i)) {
        newItems.push(disabledPositions.get(i)!);
      } else {
        newItems.push(enabledItems[eIdx++]);
      }
    }

    const movedLabel = items[event.previousIndex]?.label || 'Field';
    this.items.set(newItems);
    if (enabledPrevIdx !== enabledCurrIdx) {
      announceListItemMoved({
        announcer: this.liveAnnouncer,
        label: movedLabel,
        newIndex: event.currentIndex,
        total: newItems.length,
        delta: event.currentIndex > event.previousIndex ? 1 : -1,
        orientation: 'horizontal',
      });
    }
    this.updateReport();
  }

  private fieldsForSection(
    section: 'default' | 'WC' | 'LI' | 'PR',
  ): Field[] {
    switch (section) {
      case 'WC':
        return this.wcFields();
      case 'LI':
        return this.liFields();
      case 'PR':
        return this.prFields();
      default:
        return this.defaultFields();
    }
  }

  dropInSection(
    event: CdkDragDrop<unknown>,
    section: 'default' | 'WC' | 'LI' | 'PR',
  ): void {
    const sectionFields = this.fieldsForSection(section);

    if (sectionFields[event.currentIndex]?.disabled) {
      this.snackbar.show('Locked fields cannot be reordered', 'Info');
      return;
    }

    const enabledSectionItems: Field[] = [];
    const disabledSectionPositions = new Map<number, Field>();

    sectionFields.forEach((item, idx) => {
      if (item.disabled) {
        disabledSectionPositions.set(idx, item);
      } else {
        enabledSectionItems.push(item);
      }
    });

    let enabledPrevIdx = 0;
    let enabledCurrIdx = 0;
    let enabledCount = 0;
    for (let i = 0; i < sectionFields.length; i++) {
      if (!sectionFields[i].disabled) {
        if (i === event.previousIndex) enabledPrevIdx = enabledCount;
        if (i === event.currentIndex) enabledCurrIdx = enabledCount;
        enabledCount++;
      }
    }

    moveItemInArray(enabledSectionItems, enabledPrevIdx, enabledCurrIdx);

    const newSectionFields: Field[] = [];
    let eIdx = 0;
    for (let i = 0; i < sectionFields.length; i++) {
      if (disabledSectionPositions.has(i)) {
        newSectionFields.push(disabledSectionPositions.get(i)!);
      } else {
        newSectionFields.push(enabledSectionItems[eIdx++]);
      }
    }

    const allItems = this.items();
    const defaultItems = allItems.filter((f) => !f.metadata?.customSection);
    const wcItems = allItems.filter((f) => f.metadata?.customSection === 'WC');
    const liItems = allItems.filter((f) => f.metadata?.customSection === 'LI');
    const prItems = allItems.filter((f) => f.metadata?.customSection === 'PR');

    const orderedDefault =
      section === 'default' ? newSectionFields : defaultItems;
    const orderedWc = section === 'WC' ? newSectionFields : wcItems;
    const orderedLi = section === 'LI' ? newSectionFields : liItems;
    const orderedPr = section === 'PR' ? newSectionFields : prItems;

    const newItems = [
      ...orderedDefault,
      ...orderedWc,
      ...orderedLi,
      ...orderedPr,
    ];

    const movedLabel = sectionFields[event.previousIndex]?.label || 'Field';
    this.items.set(newItems);
    if (enabledPrevIdx !== enabledCurrIdx) {
      announceListItemMoved({
        announcer: this.liveAnnouncer,
        label: movedLabel,
        newIndex: event.currentIndex,
        total: newSectionFields.length,
        delta: event.currentIndex > event.previousIndex ? 1 : -1,
        orientation: 'horizontal',
      });
    }
    this.updateReport();
  }

  moveField(
    idOrIndex: string | number,
    delta: -1 | 1,
    activator?: Event,
  ): void {
    activator?.stopPropagation();
    const list = this.items();
    const index =
      typeof idOrIndex === 'number'
        ? idOrIndex
        : list.findIndex((f) => f.id === idOrIndex);
    if (index < 0) {
      return;
    }
    const target = this.neighborIndex(list, index, delta);
    const moved = list[index];
    if (target === -1) return;

    this.drop({
      previousIndex: index,
      currentIndex: target,
    } as CdkDragDrop<unknown>);

    if (activator && moved?.id) {
      scheduleRestoreMoveButtonFocus({
        containerSelector: `app-drag-drop-fields [data-move-id="${CSS.escape(moved.id)}"]`,
        delta,
      });
    }
  }

  moveFieldInSection(
    section: 'default' | 'WC' | 'LI' | 'PR',
    idOrIndex: string | number,
    delta: -1 | 1,
    activator?: Event,
  ): void {
    activator?.stopPropagation();
    const sectionFields = this.fieldsForSection(section);
    const sectionIndex =
      typeof idOrIndex === 'number'
        ? idOrIndex
        : sectionFields.findIndex((f) => f.id === idOrIndex);
    if (sectionIndex < 0) {
      return;
    }
    const moved = sectionFields[sectionIndex];
    const target = this.neighborIndex(sectionFields, sectionIndex, delta);
    if (target === -1) return;

    this.dropInSection(
      {
        previousIndex: sectionIndex,
        currentIndex: target,
      } as CdkDragDrop<unknown>,
      section,
    );

    if (activator && moved?.id) {
      scheduleRestoreMoveButtonFocus({
        containerSelector: `app-drag-drop-fields [data-move-id="${CSS.escape(moved.id)}"]`,
        delta,
      });
    }
  }

  neighborIndex(list: Field[], index: number, delta: -1 | 1): number {
    for (
      let i = index + delta;
      delta < 0 ? i >= 0 : i < list.length;
      i += delta
    ) {
      if (!list[i]?.disabled) return i;
    }
    return -1;
  }

  setField(item: Field): void {
    this.currentItem = item;
    const report = this.report();
    const reportId = report?.reportTemplate?.id ?? null;
    const index = this.items().indexOf(item);
    const ipColumn = index >= 0 ? index + 1 : null;
    this.dialog.show(reportId, ipColumn);
  }

  selected(selectedField: ReportField): void {
    if (this.currentItem) {
      const items = this.items();
      const index = items.indexOf(this.currentItem);
      if (index !== -1) {
        const report = this.report();
        const isDuplicate = items.some(
          (item, i) =>
            i !== index &&
            item.field &&
            !item.isPlaceholder &&
            item.metadata?.tableAlias === selectedField.tableAlias &&
            item.metadata?.columnName === selectedField.columnName,
        );
        if (isDuplicate) {
          this.snackbar.show('Error : Duplicate', 'Error');
          return;
        }

        if (report.reportSections?.preventDuplicateFieldsAndSorts) {
          const sortFields = report.reportRequest?.sortBreakOptions ?? [];
          const isSortDuplicate = sortFields.some(
            (sbo) =>
              sbo.tableAlias === selectedField.tableAlias &&
              sbo.columnName === selectedField.columnName,
          );
          if (isSortDuplicate) {
            this.snackbar.show('Duplicate', 'Error');
            return;
          }
        }

        const newItems = [...items];

        const origSlotLabel =
          newItems[index].metadata?.defaultValue ??
          newItems[index].label ??
          'Custom Field';

        const completeMetadata: ReportField = {
          canDelete: newItems[index].canDelete,
          columnId: selectedField.columnId,
          columnName: selectedField.columnName,
          customSection: selectedField.customSection,
          defaultValue: origSlotLabel,
          selectedFieldLabel: selectedField.defaultValue,
          displayOrder: index + 1,
          inlineFilterType: selectedField.inlineFilterType,
          pageBreakChecked: selectedField.pageBreakChecked,
          tableAlias: selectedField.tableAlias,
          updateable: newItems[index].updateable,
        };

        newItems[index] = new Field(
          selectedField.defaultValue,
          completeMetadata.columnName,
          newItems[index].disabled,
          completeMetadata.canDelete,
          completeMetadata.updateable,
          completeMetadata,
        );
        this.items.set(newItems);
        this.updateReport();
      }
    }
  }

  clearField(item: Field): void {
    const items = this.items();
    const index = items.indexOf(item);
    if (index !== -1) {
      const newItems = [...items];

      const origMeta = item.metadata;
      const placeholderLabel = 'Empty'; // Will be corrected by renumber

      const slotNumber = String(origMeta?.displayOrder ?? index + 1).padStart(
        2,
        '0',
      );
      const nullColumnName = `NULL_${slotNumber}`;

      const defaultMetadata: ReportField | undefined = origMeta
        ? {
            canDelete: origMeta.canDelete,
            columnId: origMeta.columnId,
            columnName: nullColumnName,
            customSection: origMeta.customSection,
            defaultValue: placeholderLabel,
            displayOrder: origMeta.displayOrder,
            inlineFilterType: origMeta.inlineFilterType,
            pageBreakChecked: false,
            tableAlias: '',
            updateable: origMeta.updateable,
            selectedFieldLabel: undefined,
          }
        : undefined;

      newItems[index] = new Field(
        placeholderLabel,
        nullColumnName,
        !item.updateable,
        item.canDelete,
        item.updateable,
        defaultMetadata,
        true,
      );

      this.renumberCustomFieldLabels(newItems);
      this.items.set(newItems);
      this.updateReport();
    }
  }

  private renumberCustomFieldLabels(items: Field[]): void {
    const isPlaceholderField = (item: Field) =>
      item.isPlaceholder || !!item.metadata?.columnName?.includes('NULL_');

    let labelPrefix: string | null = null;
    for (const item of items) {
      if (isPlaceholderField(item) && item.label) {
        const match = item.label.match(/^(.+?)\s+(\d+)$/);
        if (match) {
          labelPrefix = match[1];
          break;
        }
      }
    }

    if (!labelPrefix) return;

    items.forEach((item, index) => {
      if (isPlaceholderField(item)) {
        const rank = index + 1;
        const strRank = rank < 10 ? '0' + rank : String(rank);
        const newLabel = `${labelPrefix} ${strRank}`;
        item.label = newLabel;
        if (item.metadata) {
          item.metadata = {
            ...item.metadata,
            defaultValue: newLabel,
            selectedFieldLabel: undefined,
          };
        }
      }
    });
  }

  private updateReport(): void {
    const fieldsWithMetadata = this.items()
      .filter((item) => (item.field || item.isPlaceholder) && item.metadata)
      .map((item, index) => ({
        ...item.metadata!,
        displayOrder: index + 1,
      }));

    this.reports.currentReport.update((report) => {
      if (report.reportRequest) {
        if (
          report.reportSections?.hasCustomFinancials ||
          report.reportSections?.hasCustomNonFinancials
        ) {
          const otherFields = (report.reportRequest.reportFields ?? []).filter(
            (f) =>
              f.inlineFilterType === 'COMP_FINANCIAL' ||
              f.inlineFilterType === 'COMP_NON_FINANCIAL' ||
              this.isDyoOutputField(f),
          );
          report.reportRequest.reportFields = [
            ...fieldsWithMetadata,
            ...otherFields,
          ];
        } else {
          report.reportRequest.reportFields = fieldsWithMetadata;
        }
      }
      return report;
    });
    this.fieldsChanged.emit();
  }
}
